import { Action, Dispatch } from 'redux';
import { IAppState } from '../reducers';
import {
  clearSelectedTeams, ActionType as FormAction,
  toggleGameIdInUse, updateGameId,
  updateFormType, FormType,
  updateSelectedSeason,
} from './gameScheduleForm';
import {
  createGame as createGameApi, gameIdInUse,
  getTeamGameNumber, getGameId,
} from '../../api/vault/exchangeSchedule';
import toast from '../../utils/toast';
import { Sport } from '../../enums';
import { IProps } from '../../components/Exchange/CreateGameScheduleDialog/CreateGameSchedule/interfaces';
import { getCurrentYear } from '../../utils/time';

export interface IState {
  isOpen: boolean;
  isCreating: boolean;
}

export enum ActionType {
  Toggle = 'CreateGameScheduleDialog.Toggle',
  ClearAll = 'CreateGameScheduleDialog.ClearAll',
  ToggleCreating = 'CreateGameScheduleDialog.ToggleCreating',
}

export interface ICreateGameScheduleDialogAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isCreating: false,
};

export default function reducer(state: IState = INITIAL_STATE, action: ICreateGameScheduleDialogAction): IState {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.ClearAll:
      return {
        ...INITIAL_STATE,
      };
    case ActionType.ToggleCreating:
      return {
        ...state,
        isCreating: !state.isCreating,
      };
    default:
      return state;
  }
}

export const toggleCreateGameScheduleDialog = () => (dispatch: Dispatch, getState: () => IAppState) => {
  const {
    exchange: {
      createGameScheduleDialog: {
        isOpen,
      },
    },
  } = getState();
  if (!isOpen) {
    dispatch(updateFormType(FormType.Create));
    updateSelectedSeason(getCurrentYear.toString())(dispatch, getState);
  }
  dispatch({ type: ActionType.Toggle });
};

export const clearAll = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({ type: FormAction.ClearAll });
  dispatch({ type: ActionType.ClearAll });
};

export const createGame = () => async (dispatch: Dispatch, getState: () => IAppState) => {
  const {
    exchange: {
      gameScheduleForm: {
        schedules,
        gameDate, gameId,
        homeTeam, awayTeam,
        selectedSeason,
        isActive,
        selectedSport,
        selectedLeague,
        selectedExchangeGameType: selectedGameType,
      },
    },
  } = getState();

  dispatch({ type: ActionType.ToggleCreating });

  const { data: { data: inUse } } = await gameIdInUse(gameId, selectedSport, selectedLeague);
  if (inUse && selectedSport !== Sport.Softball) {
    toast('Game with matching gameId already exists', { type: toast.TYPE.ERROR });
    dispatch(toggleGameIdInUse());
    dispatch({ type: ActionType.ToggleCreating });
    return;
  }

  const match = selectedSport !== Sport.Softball ? schedules.find(({
    Season,
    HomeTeamCode,
    VisitorTeamCode,
    GameDate,
  }) => Season === selectedSeason
  && HomeTeamCode === homeTeam.TeamCode
  && awayTeam.TeamCode === VisitorTeamCode
    && new Date(gameDate).getTime() === new Date(GameDate).getTime()) : null;

  if (!match) {
    // note: fetch most recent team game numbers prior to creation
    const { data: { data: { teamGameNumber: homeGameNumber } } } = await getTeamGameNumber(homeTeam._id, selectedSeason, gameDate);
    const { data: { data: { teamGameNumber: visitorGameNumber } } } = await getTeamGameNumber(awayTeam._id, selectedSeason, gameDate);

    try {
      const newGame = await createGameApi({
        Season: selectedSeason,
        GameDate: gameDate,
        HomeTeamGameNumber: homeGameNumber,
        VisitorTeamGameNumber: visitorGameNumber,
        IsActive: isActive,
        HomeTeamCode: homeTeam.TeamCode,
        HomeTeamId: homeTeam._id,
        VisitorTeamCode: awayTeam.TeamCode,
        VisitorTeamId: awayTeam._id,
        NCAAGameNumber: gameId,
        Sport: selectedSport,
        League: selectedLeague,
        GameType: selectedGameType,
      });

      const {
        data: {
          data: {
            NCAAGameNumber, GameDate,
            VisitorTeamName, HomeTeamName,
          },
        },
      } = newGame;

      if (NCAAGameNumber) {
        toast(`Game Created Successfully\n ${NCAAGameNumber} ${HomeTeamName} @ ${VisitorTeamName} ${GameDate}`, { type: toast.TYPE.INFO });

        dispatch(clearSelectedTeams());
        const { data: { data: { gameId: newGameId } } } = await getGameId(selectedSeason, selectedSport, selectedLeague);
        dispatch(updateGameId(newGameId));
      } else {
        toast('Error occurred while creating game', { type: toast.TYPE.ERROR });
      }
    } catch (e) {
      const error = e as any;
      const message = error?.response?.data?.message || 'Error occurred while creating game';
      toast(message, { type: toast.TYPE.ERROR });
    }
  } else {
    toast('Game with matching criteria already exists', { type: toast.TYPE.ERROR });
  }
  dispatch({ type: ActionType.ToggleCreating });
};

export const selectCanCreateGame = (state: IAppState): boolean => {
  const {
    exchange: {
      gameScheduleForm: {
        homeTeam,
        awayTeam,
        gameDate,
        homeGameNumber,
        visitorGameNumber,
        selectedSeason: season,
        inUseGameId,
        selectedSport,
        selectedLeague,
        selectedExchangeGameType: selectedGameType,
      },
    },
  } = state;

  return !!(homeTeam && awayTeam && gameDate && homeGameNumber && visitorGameNumber && season && !inUseGameId && selectedSport && selectedLeague && selectedGameType);
};

export const checkUpdateGameId = (prevProps?: IProps) => async (dispatch: Dispatch, getState: () => IAppState) => {
  const {
    exchange: {
      gameScheduleForm: {
        selectedSport, selectedLeague, selectedSeason,
      },
    },
  } = getState();

  if (selectedSeason && selectedSport && selectedLeague) {
    if (prevProps) {
      if (prevProps.selectedSeason !== selectedSeason || prevProps.selectedSport !== selectedSport || prevProps.selectedLeague !== selectedLeague) {
        const { data: { data: { gameId } } } = await getGameId(selectedSeason, selectedSport, selectedLeague);
        dispatch(updateGameId(gameId));
        dispatch(clearSelectedTeams());
      }
    } else {
      const { data: { data: { gameId } } } = await getGameId(selectedSeason, selectedSport, selectedLeague);
      dispatch(updateGameId(gameId));
    }
  }
};
